import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";

import { ITeeTime } from "redux/reducers/models/teetime";

import { useAppSelector } from "hooks/redux";
import { convertTime, formatDate } from "helpers/Helpers";

import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Popover from "components/popover/Popover";
import { Select } from "components/select/index";

import { useTranslation } from "react-i18next";
import Spin from "components/spin/spin";
import { GetTeeTime as GetClientTeeTime } from "api/rpc/2024-04/clientAdmin/teesheet/teeSheet";
import { GetTeeTime as GetFacilityTeeTime } from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";

interface IDuplicateBookingTab {
  isActive: boolean;
  copyBookingFutureDates: any[];
  bookingStateHoles: number;
  bookingStateQuantity: number;
  addCopyBookingIdToList: (copyBookingId: number, copyBookingTime: string, copyBookingDate: string) => void;
  confirmCopyBooking: (copyBookingId: number) => void;
  userLevel: "facility" | "client";
};

export default function DuplicateBookingTab(props: IDuplicateBookingTab) {
  const { Option } = Select;
  const { t } = useTranslation();
  const teeSheetStore = useAppSelector(store =>
    props.userLevel === "facility" ? store.teeSheetStore : store.clientTeeSheetStore,
  );
  const facilityStore = useAppSelector(store =>
    props.userLevel === "facility" ? store.facilityStore : store.clientFacilityStore,
  );
  const [date, setDate] = useState<Date | null>(new Date());
  const [loadingTeeTimes, setLoadingTeeTimes] = useState(false);
  const [duplicateBooking, setDuplicateBooking] = useState({
    copyBookingId: null,
    copyBookingTime: null,
    copyBookingDate: null,
  });

  const [allAvailableTeeTimes, setAllAvailableTeeTimes] = useState([]);
  const availableTimes = useMemo(() => {
    const returnData: Partial<ITeeTime>[] = [];

    if (props.bookingStateHoles === 18) {
      allAvailableTeeTimes.map((teetime: ITeeTime) => {
        if (
          teetime.blocked_type === "open" &&
          teetime.divisions_available.includes(2) &&
          teetime.turn_tee_time !== null
        ) {
          returnData.push({
            id: teetime.id,
            start_time: convertTime(teetime.start_time),
            course_id: teetime.course_id,
            tournament_id: teetime.tournament_id,
            league_id: teetime.league_id,
          });
        }
      });
    } else {
      allAvailableTeeTimes.map((teetime: ITeeTime) => {
        if (teetime.blocked_type === "open") {
          returnData.push({
            id: teetime.id,
            start_time: convertTime(teetime.start_time),
            course_id: teetime.course_id,
            tournament_id: teetime.tournament_id,
            league_id: teetime.league_id,
          });
        }
      });
    }
    return returnData;
  }, [allAvailableTeeTimes, props.bookingStateHoles]);

  //handle async times dependant on date
  useEffect(() => {
    if (date !== null) {
      void updateAvailableTeeTimes();
    }
  }, [date]);

  //reset if Tab opens/closes
  useEffect(() => {
    resetDuplicateBookingState();
    syncDateWithState(date);
  }, [props.isActive]);

  async function updateAvailableTeeTimes() {
    const formattedDate = formatDate(date);
    const apiParams = {
      facility_id: props.userLevel === "client" ? facilityStore.facility?.id : undefined,
      date: formattedDate,
      quantity_remaining: props.bookingStateQuantity,
      turn_tee_time: true,
    };

    // API call dependant on userLevel
    switch (props.userLevel) {
      case "facility": {
        const teeTimeRes = await GetFacilityTeeTime(apiParams, false);
        if (teeTimeRes.status === StatusCode.OK) {
          console.log("moveBooking facility API ...", teeTimeRes); //debug
          setAllAvailableTeeTimes(teeTimeRes.data);
          setLoadingTeeTimes(true);
        }
        break;
      }
      case "client": {
        const teeTimeRes = await GetClientTeeTime(apiParams, false);
        if (teeTimeRes.status === StatusCode.OK) {
          console.log("moveBooking client API ...", teeTimeRes); //debug
          setAllAvailableTeeTimes(teeTimeRes.data);
          setLoadingTeeTimes(true);
        }
        break;
      }
      default:
        setAllAvailableTeeTimes([]);
    }
  }

  function syncDateWithState(returnDate: Date) {
    const copyDateArr = String(returnDate).split(" ");
    const copyDate = copyDateArr[1] + " " + copyDateArr[2];

    if (returnDate !== date) {
      setDate(returnDate);
      setLoadingTeeTimes(false);
    }

    setDuplicateBooking({ ...duplicateBooking, copyBookingDate: copyDate });
  }

  function handleTimeChange(incomingValue: number) {
    const foundTimeSlot = availableTimes.find(time => time.id === incomingValue);

    setDuplicateBooking({
      ...duplicateBooking,
      copyBookingId: incomingValue,
      copyBookingTime: foundTimeSlot?.start_time,
    });
  }

  function handleConfirmCopyBooking() {
    props.confirmCopyBooking(duplicateBooking.copyBookingId as number);
    resetDuplicateBookingState();
  }

  function handleCopyBookingIdToList() {
    props.addCopyBookingIdToList(
      duplicateBooking.copyBookingId as number,
      duplicateBooking.copyBookingTime as string,
      duplicateBooking.copyBookingDate as string,
    );
    resetDuplicateBookingState();
  }

  const resetDuplicateBookingState = () => {
    setDuplicateBooking({
      ...duplicateBooking,
      copyBookingId: null,
      copyBookingTime: null,
    });
  };

  return (
    <div className="bm-copy-booking-popover-container">
      <Popover active={props.isActive}>
        <div className="bm-copy-booking-container">
  
          <p className="bm-copy-booking-title">{t("Duplicate booking")}</p>
  
          <hr />
  
          {/* DATE FIELD */}
          <div className="bm-copy-field-wrapper">
            {/* <FontAwesomeIcon className="bm-copy-icon" icon={["far", "calendar"]} /> */}
            <DatePickerInput
              months={1}
              position="left"
              startingDate={date}
              disablePastDays
              setStartingDate={(d: Date) => syncDateWithState(d)}
              //rightAlignedCalendar
            />
          </div>
  
          {/* TIME FIELD */}
          {loadingTeeTimes ? (
            <div className="bm-copy-field-wrapper">
              <FontAwesomeIcon className="bm-copy-icon" icon={["far", "clock"]} />
              <Select
                className="bm-copy-booking-select"
                onChange={(v: number) => handleTimeChange(v)}
                defaultValue={"0"}
              >
                <Option value="0">{t("Select time")}</Option>
  
                {availableTimes
                  .filter(t => t.course_id === teeSheetStore.selectedTeeTime?.course_id)
                  .map(teetime => (
                    <Option key={teetime.id} value={teetime.id}>
                      {teetime.start_time}
                    </Option>
                  ))}
              </Select>
            </div>
          ) : (
            <div style={{ marginTop: "20px", height: "40px" }}>
              <Spin className="self-center" />
            </div>
          )}
  
          {/* BUTTON ROW */}
          <div className="bm-copy-buttons-row">
            <button
              type="button"
              className="bm-copy-add-button"
              onClick={handleCopyBookingIdToList}
            >
              {t("Add")}
            </button>
  
            <button
              type="button"
              className="bm-copy-confirm-button"
              onClick={handleConfirmCopyBooking}
            >
              {t("Duplicate")}
            </button>
          </div>
  
        </div>
      </Popover>
    </div>
  );
  
  
}
